import { useCallback, useEffect, useState } from 'react'
import { ApiClientError, type Credential, type PipelineDetail, type PipelineVersion } from '../api/types.js'
import { Editor, type EditorClient } from '../editor/Editor.js'
import { ImportExport } from '../editor/ImportExport.js'
import { VersionCompare, type VersionsClient } from '../editor/VersionCompare.js'
import { EMPTY_GRAPH, type Graph } from '../editor/graph.js'
import type { NodeDefinitionView } from '../editor/definitions.js'
import { Alert } from '../ui/Alert.js'
import { useToaster } from '../ui/toasts.js'
import type { WorkspaceStore } from '../ui/workspace.js'

export interface PipelineEditorClient extends EditorClient, VersionsClient {
  pipeline(id: string): Promise<PipelineDetail>
  saveDraft(id: string, graph: unknown): Promise<PipelineDetail>
  publishPipeline(id: string): Promise<PipelineVersion>
  nodeDefinitions(): Promise<NodeDefinitionView[]>
  credentials(): Promise<Credential[]>
  instances(): Promise<{ items: { id: string; name: string; provider: string }[] }>
  /** Сценарии арендатора — для выбора подсценария (FR-PIPE-021). */
  pipelines(): Promise<{ items: { id: string; name: string }[] }>
}

export interface PipelineEditorProps {
  client: PipelineEditorClient
  pipelineId: string
  permissions: string[]
  /** Настройки рабочего места: свёрнута ли палитра нод (FR-ADM-044). */
  workspace?: WorkspaceStore
}

const CAN_WRITE = 'pipelines.write'

const GENERIC_FAILURE = 'Не удалось выполнить действие. Проверьте соединение и попробуйте ещё раз.'

function messageOf(cause: unknown): string {
  return cause instanceof ApiClientError ? cause.message : GENERIC_FAILURE
}

/**
 * Скачивание файла: браузерный способ, а не свой. Ссылка с `download`
 * работает и там, где всплывающие окна запрещены.
 */
function download(content: string, fileName: string): void {
  const url = URL.createObjectURL(new Blob([content], { type: 'application/json' }))
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  link.click()
  URL.revokeObjectURL(url)
}

/**
 * Экран одного сценария: редактор, перенос файлом и версии. Собирает то, что
 * задачи 17-21 сделали по отдельности, и подключает к службе — до этого
 * экрана редактор нельзя было открыть вовсе.
 */
export function PipelineEditor(props: PipelineEditorProps) {
  const { client, pipelineId, permissions, workspace } = props
  const canWrite = permissions.includes(CAN_WRITE)
  const toaster = useToaster()

  const [pipeline, setPipeline] = useState<PipelineDetail | null>(null)
  const [graph, setGraph] = useState<Graph>(EMPTY_GRAPH)
  const [definitions, setDefinitions] = useState<NodeDefinitionView[]>([])
  const [credentials, setCredentials] = useState<Credential[]>([])
  const [instances, setInstances] = useState<{ id: string; name: string; provider: string }[]>(
    [],
  )
  const [workflows, setWorkflows] = useState<{ id: string; name: string }[]>([])
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(() => {
    let cancelled = false
    Promise.all([
      client.pipeline(pipelineId),
      client.nodeDefinitions(),
      client.credentials(),
      client.instances(),
      client.pipelines(),
    ])
      .then(([detail, nodes, creds, instancePage, pipelinePage]) => {
        if (cancelled) return
        setPipeline(detail)
        // Черновик, если он есть, — иначе пустой холст: опубликованную
        // версию править нельзя, она неизменяема (FR-PIPE-001).
        setGraph((detail.draftGraph ?? EMPTY_GRAPH) as Graph)
        setDefinitions(nodes)
        setCredentials(creds)
        setInstances(instancePage.items)
        // Сам себя сценарий вызывать не предлагает: это всегда рекурсия,
        // и предел вложенности её остановит, но выбирать её незачем.
        setWorkflows(pipelinePage.items.filter((item) => item.id !== pipelineId))
        setError(null)
      })
      .catch((cause: unknown) => {
        if (!cancelled) setError(messageOf(cause))
      })
    return () => {
      cancelled = true
    }
  }, [client, pipelineId])

  useEffect(load, [load])

  const saveDraft = useCallback(
    (next: Graph) => {
      void client.saveDraft(pipelineId, next).catch((cause: unknown) => {
        // Молчаливый отказ сохранения — потерянная работа: человек правит
        // дальше, считая, что черновик записан. Уведомлением, а не полосой
        // поверх редактора: сохранение идёт само, у него нет своего места
        // на экране, а полоса сдвинула бы холст под курсором.
        toaster.failed(messageOf(cause))
      })
    },
    [client, pipelineId, toaster],
  )

  async function publish() {
    setError(null)
    try {
      const version = await client.publishPipeline(pipelineId)
      toaster.succeeded(`Сценарий опубликован — версия ${version.number}`)
    } catch (cause: unknown) {
      // Непройденная валидация приходит отказом 422: замечания к графу уже
      // видны на холсте, здесь показывается сама причина отказа.
      setError(messageOf(cause))
    }
  }

  if (error !== null && pipeline === null) return <Alert>{error}</Alert>
  if (pipeline === null) return <p>Загрузка сценария…</p>

  // Без права на запись черновик не сохраняется вовсе: посылать запрос, на
  // который придёт 403, значит показывать отказ на каждую правку.
  const saving = canWrite ? { onSaveDraft: saveDraft } : {}

  return (
    <section aria-label={`Сценарий: ${pipeline.name}`}>
      {error !== null && <Alert onDismiss={() => setError(null)}>{error}</Alert>}

      {/* Панель сценария: слева — куда вернуться и что открыто, справа —
          действия над сценарием целиком. Одна строка вместо трёх россыпей
          кнопок: публикация, выгрузка и загрузка делают одно и то же —
          распоряжаются сценарием, — и стоять должны рядом. */}
      <header className="сценарий__шапка">
        <a className="сценарий__назад" href="/app/pipelines">
          ← Сценарии
        </a>
        <h2>{pipeline.name}</h2>
        <span className={`badge badge--${pipeline.status === 'published' ? 'ok' : 'neutral'}`}>
          {pipeline.status === 'published' ? 'Опубликован' : 'Черновик'}
        </span>
        <div className="сценарий__действия">
          <ImportExport graph={graph} onDownload={download} onImported={setGraph} />
          {canWrite && (
            <button type="button" className="btn btn--primary" onClick={() => void publish()}>
              Опубликовать
            </button>
          )}
        </div>
      </header>

      <Editor
        definitions={definitions}
        graph={graph}
        onGraphChange={setGraph}
        {...saving}
        pipelineId={pipelineId}
        client={client}
        permissions={permissions}
        instances={instances}
        workflows={workflows}
        credentials={credentials.map((credential) => ({
          id: credential.id,
          name: credential.name,
          type: credential.type,
        }))}
        {...(workspace === undefined ? {} : { workspace })}
      />

      {/* История версий — внизу и свёрнутая: она нужна, когда что-то
          пошло не так, а не всё время, а холст нужен всегда. Раскрытая,
          она занимает нижнюю треть и прокручивается сама. */}
      <details className="версии-ящик">
        <summary>История версий и сравнение</summary>
        <VersionCompare client={client} pipelineId={pipelineId} draft={graph} />
      </details>
    </section>
  )
}
